import React, { useEffect, useState } from 'react';
import {
    Box,
    VStack,
    HStack,
    Text,
    Icon,
    Badge,
    SimpleGrid,
    Image,
    Flex,
    Tooltip,
    Divider,
    Spinner
} from '@chakra-ui/react';
import { FiDollarSign, FiPieChart, FiClock, FiTrendingUp } from 'react-icons/fi';
import SolanaLogo from './SolanaLogo';
import SeaCreatureIcon from './SeaCreatureIcon';
import { useContribution } from '../context/ContributionContext';
import { useAuctionState } from '../context/AuctionStateContext';
import priceService from '../services/priceService';
import { getSimulatedData } from '../utils/simulatedData';
import priceCalculator from '../utils/priceCalculator';

const formatNumber = (num) => {
    if (num === undefined || num === null || isNaN(num)) {
        return '0';
    }
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(num);
};

const getRankBadge = (contribution) => {
    if (contribution >= 50) return { 
        rank: 'Blue Whale',
        emoji: '🐳',
        color: '#ff1f69'
    };
    if (contribution >= 30) return { 
        rank: 'Whale',
        emoji: '🐋',
        color: '#ff9f1f'
    };
    if (contribution >= 20) return { 
        rank: 'Shark',
        emoji: '🦈',
        color: '#ffe248'
    };
    if (contribution >= 10) return { 
        rank: 'Dolphin',
        emoji: '🐬',
        color: '#7ed957'
    };
    if (contribution >= 5) return { 
        rank: 'Fish',
        emoji: '🐟',
        color: '#38b6ff'
    };
    if (contribution >= 3) return { 
        rank: 'Tropical Fish',
        emoji: '🐠',
        color: '#fd2626'
    };
    if (contribution >= 2) return { 
        rank: 'Blowfish',
        emoji: '🐡',
        color: '#ff914d'
    };
    if (contribution >= 1) return { 
        rank: 'Octopus',
        emoji: '🐙',
        color: '#ffde59'
    };
    if (contribution >= 0.5) return { 
        rank: 'Lobster',
        emoji: '🦞',
        color: '#2fdb88'
    };
    if (contribution >= 0.1) return { 
        rank: 'Shrimp',
        emoji: '🦐',
        color: '#159fef'
    };
    return { 
        rank: 'No Rank',
        emoji: '🏷️',
        color: '#e2e8f0'
    };
};

const SALE_START_DATE = new Date('2024-12-10');
const TOTAL_DAYS = 50;

const UserStatsBox = ({
    userContribution = 0,
    pendingContribution = 0,
    totalUserJinu = 0,
    pendingJinu = 0,
    dailyAllocations = [],
    completedSocialTasks = [],
    currentDay = 16,
    hasConfirmedContributions = false,
    hasProjectedContributions = false,
    userContributions = {}
}) => {
    const TOTAL_JINU_SUPPLY = 37500000; // 37.5M JINU
    const totalContribution = userContribution + pendingContribution;
    const rankBadge = getRankBadge(totalContribution);
    
    // Calculate user's total JINU and share percentage
    const userJinuAllocation = totalUserJinu || 0;
    const totalJinuAllocation = userJinuAllocation + (pendingJinu || 0);
    const sharePercentage = totalJinuAllocation > 0 
        ? ((totalJinuAllocation / TOTAL_JINU_SUPPLY) * 100)
        : 0;

    // Format JINU values for display
    const formatJinuValue = (value) => {
        if (value === undefined || value === null || isNaN(value)) return "0";
        if (value >= 1000000) return `${formatNumber(value / 1000000, 2)}M`;
        if (value >= 1000) return `${formatNumber(value / 1000, 2)}K`;
        return formatNumber(value);
    };

    const { solPrice, formatUsd, solToUsd } = useContribution();
    const auctionState = useAuctionState();
    const [realTimeSolPrice, setRealTimeSolPrice] = useState(solPrice);
    const [totalSimulatedSol, setTotalSimulatedSol] = useState(0);
    const [averageEntryPrice, setAverageEntryPrice] = useState(null);

    // Calculate total simulated SOL
    useEffect(() => {
        // Sum up all simulated SOL from all days
        let totalSol = 0;
        for (let day = 1; day <= 50; day++) {
            const simData = getSimulatedData(day);
            totalSol += simData.sol || 0;
        }
        setTotalSimulatedSol(totalSol);
        console.log('Total simulated SOL:', totalSol);
    }, []);

    // Fetch real-time SOL price
    useEffect(() => {
        const fetchRealTimeSolPrice = async () => {
            const price = await priceService.getSolanaPrice();
            setRealTimeSolPrice(price);
            console.log('Real-time SOL price:', price);
        };
        fetchRealTimeSolPrice();
    }, []);
    
    // Calculate average entry price from all contributions
    useEffect(() => {
        console.log('Checking contributions:', {
            hasConfirmedContributions,
            userContributions
        });
        
        if (!hasConfirmedContributions) {
            setAverageEntryPrice(null);
            return;
        }
        
        let totalAmount = 0;
        let weightedPriceSum = 0;
        
        // Loop through all days and contributions
        Object.entries(userContributions || {}).forEach(([day, contributions]) => {
            console.log(`Day ${day} contributions:`, contributions);
            contributions.forEach(contrib => {
                totalAmount += contrib.amount;
                
                // Use the stored entry price if available, otherwise use current price
                const entryPrice = contrib.entryPrice || priceCalculator.currentPrice;
                console.log(`Contribution: ${contrib.amount} SOL, Entry price: ${entryPrice}`);
                weightedPriceSum += contrib.amount * entryPrice;
            });
        });
        
        const avgPrice = totalAmount > 0 ? weightedPriceSum / totalAmount : null;
        setAverageEntryPrice(avgPrice);
        console.log('Average entry price calculation:', {
            totalAmount,
            weightedPriceSum,
            avgPrice
        });
    }, [userContributions, hasConfirmedContributions]);

    // Format SOL amount
    const formatSolAmount = (value) => {
        if (value === undefined || value === null || isNaN(value)) return "0";
        return `${formatNumber(value)} SOL`;
    };

    // Format USD amount
    const formatUsdAmount = (value) => {
        if (value === undefined || value === null || isNaN(value) || !realTimeSolPrice) return "$0";
        const usdValue = value * realTimeSolPrice;
        return `$${formatNumber(usdValue)}`;
    };

    return (
        <Box
            bg="rgba(16, 16, 16, 0.9)"
            borderRadius="xl"
            p={6}
            border="1px solid rgba(153, 69, 255, 0.3)"
            position="relative"
            overflow="hidden"
            boxShadow="0 0 40px rgba(153, 69, 255, 0.3)"
            _before={{
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                bg: "radial-gradient(circle at center, rgba(153, 69, 255, 0.15), transparent 70%)",
                pointerEvents: "none",
                zIndex: 0
            }}
            w="100%"
            h="100%"
        >
            <Box position="relative" zIndex={1}>
                <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={{ base: 4, sm: 6 }}>
                    {/* Contribution Section */}
                    <VStack align="start" spacing={4}>
                        <HStack>
                            <Icon as={FiDollarSign} color={hasConfirmedContributions ? "green.400" : "gray.400"} boxSize={{ base: 4, sm: 5 }} />
                            <Text color="gray.300" fontSize={{ base: "sm", md: "md" }}>Your Contribution</Text>
                        </HStack>
                        
                        {/* Contribution Display with Formula */}
                        {hasConfirmedContributions && (
                            <>
                                {/* If we have both confirmed and pending contributions */}
                                {hasProjectedContributions && (
                                    <VStack align="start" spacing={2} width="100%">
                                        <HStack justify="space-between" width="100%">
                                            <Text color="gray.400" fontSize="sm">Confirmed:</Text>
                                            <HStack spacing={2}>
                                                <Image
                                                    src="/solana-sol-logo.svg"
                                                    alt="SOL"
                                                    width="14px"
                                                    height="14px"
                                                />
                                                <Text fontWeight="bold" color="green.400">
                                                    {formatNumber(userContribution)} SOL
                                                </Text>
                                                <Text color="gray.400">|</Text>
                                                <Text fontWeight="bold" color="green.400">
                                                    {formatUsdAmount(userContribution)}
                                                </Text>
                                            </HStack>
                                        </HStack>
                                        
                                        <HStack justify="space-between" width="100%">
                                            <Text color="gray.400" fontSize="sm">Pending:</Text>
                                            <HStack spacing={2}>
                                                <Image
                                                    src="/solana-sol-logo.svg"
                                                    alt="SOL"
                                                    width="14px"
                                                    height="14px"
                                                />
                                                <Text fontWeight="bold" color="purple.400">
                                                    {formatNumber(pendingContribution)} SOL
                                                </Text>
                                                <Text color="gray.400">|</Text>
                                                <Text fontWeight="bold" color="purple.400">
                                                    {formatUsdAmount(pendingContribution)}
                                                </Text>
                                            </HStack>
                                        </HStack>
                                        
                                        <Divider borderColor="gray.700" />
                                        
                                        <HStack justify="space-between" width="100%">
                                            <Text color="gray.400" fontSize="sm">Total:</Text>
                                            <HStack spacing={2}>
                                                <Image
                                                    src="/solana-sol-logo.svg"
                                                    alt="SOL"
                                                    width="14px"
                                                    height="14px"
                                                />
                                                <Text fontWeight="bold" color="white">
                                                    {formatNumber(totalContribution)} SOL
                                                </Text>
                                                <Text color="gray.400">|</Text>
                                                <Text fontWeight="bold" color="white">
                                                    {formatUsdAmount(totalContribution)}
                                                </Text>
                                            </HStack>
                                        </HStack>
                                    </VStack>
                                )}
                                
                                {/* If we only have confirmed contributions */}
                                {!hasProjectedContributions && (
                                    <HStack spacing={2} align="center">
                                        <Image
                                            src="/solana-sol-logo.svg"
                                            alt="SOL"
                                            width="16px"
                                            height="16px"
                                        />
                                        <Text fontSize={{ base: "lg", sm: "xl" }} fontWeight="bold" color="green.400">
                                            {formatNumber(userContribution)} SOL
                                        </Text>
                                        <Text color="gray.400">|</Text>
                                        <Text fontSize={{ base: "lg", sm: "xl" }} fontWeight="bold" color="green.400">
                                            {formatUsdAmount(userContribution)}
                                        </Text>
                                    </HStack>
                                )}
                            </>
                        )}
                        
                        {/* If we only have pending contributions */}
                        {!hasConfirmedContributions && hasProjectedContributions && (
                            <HStack spacing={2} align="center">
                                <Image
                                    src="/solana-sol-logo.svg"
                                    alt="SOL"
                                    width="16px"
                                    height="16px"
                                />
                                <Text fontSize={{ base: "md", sm: "lg" }} fontWeight="bold" color="purple.400">
                                    {formatNumber(pendingContribution)} SOL
                                </Text>
                                <Text color="gray.400">|</Text>
                                <Text fontSize={{ base: "md", sm: "lg" }} fontWeight="bold" color="purple.400">
                                    {formatUsdAmount(pendingContribution)}
                                </Text>
                            </HStack>
                        )}
                        
                        {/* If we have no contributions */}
                        {!hasConfirmedContributions && !hasProjectedContributions && (
                            <HStack spacing={2} align="center">
                                <Image
                                    src="/solana-sol-logo.svg"
                                    alt="SOL"
                                    width="16px"
                                    height="16px"
                                />
                                <Text fontSize={{ base: "lg", sm: "xl" }} fontWeight="bold" color="gray.400">
                                    0 SOL
                                </Text>
                                <Text color="gray.400">|</Text>
                                <Text fontSize={{ base: "lg", sm: "xl" }} fontWeight="bold" color="gray.400">
                                    $0
                                </Text>
                            </HStack>
                        )}
                        
                        <Badge 
                            px={4}
                            py={2}
                            borderRadius="xl"
                            bg={`${rankBadge.color}15`}
                            color={rankBadge.color}
                            fontSize={{ base: "sm", sm: "md" }}
                            fontWeight="bold"
                            textTransform="none"
                            border={`1px solid ${rankBadge.color}30`}
                            _hover={{
                                bg: `${rankBadge.color}25`,
                                transform: "scale(1.02)",
                            }}
                            transition="all 0.2s"
                        >
                            <HStack spacing={2}>
                                <Text>{rankBadge.emoji}</Text>
                                <Text>{rankBadge.rank}</Text>
                            </HStack>
                        </Badge>
                    </VStack>

                    {/* Total Allocation Section */}
                    <VStack align="start">
                        <HStack>
                            <Icon as={FiPieChart} color={hasConfirmedContributions ? "green.400" : "gray.400"} boxSize={{ base: 4, sm: 5 }} />
                            <Text color="gray.300" fontSize={{ base: "sm", md: "md" }}>Your Total Allocation</Text>
                        </HStack>
                        
                        {/* JINU Allocation Display with Formula */}
                        {hasConfirmedContributions || hasProjectedContributions ? (
                            <VStack align="start" spacing={1}>
                                {/* If we have both confirmed and pending JINU */}
                                {hasConfirmedContributions && hasProjectedContributions && pendingJinu > 0 ? (
                                    <VStack align="start" spacing={0}>
                                        <HStack spacing={1}>
                                            <Text fontSize={{ base: "md", sm: "lg" }} fontWeight="bold" color="green.400">
                                                {formatJinuValue(userJinuAllocation)}
                                            </Text>
                                            <Text fontSize={{ base: "sm", sm: "md" }} color="gray.400">+</Text>
                                            <Text fontSize={{ base: "md", sm: "lg" }} fontWeight="bold" color="purple.400">
                                                {formatJinuValue(pendingJinu)}
                                            </Text>
                                        </HStack>
                                        <HStack spacing={1}>
                                            <Text fontSize={{ base: "sm", sm: "md" }} color="gray.400">=</Text>
                                            <Text fontSize={{ base: "md", sm: "lg" }} fontWeight="bold" color="white">
                                                {formatJinuValue(totalJinuAllocation)} JINU
                                            </Text>
                                        </HStack>
                                    </VStack>
                                ) : (
                                    <Text fontSize={{ base: "lg", sm: "xl" }} fontWeight="bold" 
                                        color={hasConfirmedContributions ? "green.400" : "purple.400"}>
                                        {formatJinuValue(totalJinuAllocation)} JINU
                                    </Text>
                                )}
                                
                                <Text fontSize="sm" color="gray.400">
                                    Share: {formatNumber(sharePercentage, 2)}%
                                </Text>
                            </VStack>
                        ) : (
                            <Text fontSize={{ base: "lg", sm: "xl" }} fontWeight="bold" color="gray.400">
                                0 JINU
                            </Text>
                        )}
                    </VStack>

                    {/* Days Participated Section */}
                    <VStack align="start">
                        <HStack>
                            <Icon as={FiClock} color={hasConfirmedContributions ? "green.400" : "gray.400"} boxSize={{ base: 4, sm: 5 }} />
                            <Text color="gray.300" fontSize={{ base: "sm", md: "md" }}>Days Participated</Text>
                        </HStack>
                        
                        {/* Days Participated Display */}
                        {hasConfirmedContributions ? (
                            <Text fontSize={{ base: "lg", sm: "xl" }} fontWeight="bold" color="green.400">
                                {50 - currentDay + 1} days
                            </Text>
                        ) : hasProjectedContributions ? (
                            <Text fontSize={{ base: "lg", sm: "xl" }} fontWeight="bold" color="purple.400">
                                {50 - currentDay + 1} days
                            </Text>
                        ) : (
                            <Text fontSize={{ base: "lg", sm: "xl" }} fontWeight="bold" color="gray.400">
                                {50 - currentDay + 1} days to participate
                            </Text>
                        )}
                    </VStack>

                    {/* Moonshot Section - Shows personal moonshot */}
                    <VStack align="start" spacing={4} minH="150px">
                        <HStack>
                            <Icon as={FiTrendingUp} color="green.400" boxSize={{ base: 4, sm: 5 }} />
                            <Text color="gray.300" fontSize={{ base: "sm", md: "md" }}>Moonshot</Text>
                        </HStack>
                        
                        {/* Personal Moonshot Display */}
                        <Box 
                            bg="rgba(47, 133, 90, 0.1)" 
                            p={3} 
                            borderRadius="md"
                            border="1px solid rgba(47, 133, 90, 0.3)"
                            width="100%"
                            minH="120px"
                        >
                            {totalJinuAllocation > 0 ? (
                                <VStack align="start" spacing={2}>
                                    <Text fontSize="sm" color="gray.300">
                                        Your entry price:
                                    </Text>
                                    <HStack spacing={2}>
                                        <Text fontSize="md" fontWeight="bold" color="green.400">
                                            {averageEntryPrice !== null 
                                                ? priceCalculator.formatPrice(averageEntryPrice)
                                                : priceCalculator.formatPrice(priceCalculator.currentPrice || 0.0136)}
                                        </Text>
                                        <Text color="gray.400">|</Text>
                                        <Text fontSize="md" fontWeight="bold" color="#ff0050">
                                            {priceCalculator.formatPrice(priceCalculator.currentPrice || 0.0136)}
                                        </Text>
                                        <Text color="gray.400">|</Text>
                                        <Text fontSize="md" fontWeight="bold" color="green.400">
                                            1x
                                        </Text>
                                    </HStack>
                                    
                                    <Text fontSize="xs" color="gray.500" fontStyle="italic" mt={1}>
                                        Based on {formatJinuValue(totalJinuAllocation)} JINU allocation
                                    </Text>
                                </VStack>
                            ) : (
                                <VStack align="center" justify="center" h="100%">
                                    <Text color="gray.400" fontSize="sm">
                                        Contribute to see your personal moonshot
                                    </Text>
                                </VStack>
                            )}
                        </Box>
                    </VStack>
                </SimpleGrid>
            </Box>
        </Box>
    );
};

export default UserStatsBox;
